import { useState, useEffect, useCallback } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  KeyboardAvoidingView,
  Platform,
  ActivityIndicator,
  Alert,
  TouchableOpacity,
} from 'react-native';
import { Stack, useRouter } from 'expo-router';
import { SafeAreaView } from 'react-native-safe-area-context';
import { Ionicons } from '@expo/vector-icons';

import { Colors, Shadows } from '@/constants/theme';
import { Spacing, BorderRadius } from '@/constants/spacing';
import { getBodyFont } from '@/constants/typography';
import { useColorScheme } from '@/hooks/use-color-scheme';
import { useFamilyMembers } from '@/hooks/use-family-members';
import { useMemberCalendarMapping } from '@/hooks/use-member-calendar-mapping';
import { ProfilePhotoEditor } from '@/components/profile/ProfilePhotoEditor';
import { PreferredNameEditor } from '@/components/profile/PreferredNameEditor';
import { HomeAddressEditor } from '@/components/profile/HomeAddressEditor';
import { FamilyMemberColorPicker } from '@/components/family/FamilyMemberColorPicker';
import { NotesLinkRow } from '@/components/family/NotesLinkRow';
import { ProfilePictureManager, PreferredNameManager, getFirstName, getAuthFullName } from '@/lib/profile';
import { useSupabaseAuth } from '@/lib/supabase';
import { useUserProfile } from '@/lib/user/use-user-profile';

/**
 * Edit Profile Screen
 * Full-screen view for editing user profile including:
 * - Profile photo upload/change/remove (local storage)
 * - Preferred name editing (local storage)
 */
export default function EditProfileScreen() {
  const router = useRouter();
  const colorScheme = useColorScheme();
  const palette = Colors[colorScheme ?? 'light'];
  const { session } = useSupabaseAuth();
  // Get "Me" member and family data for calendar filtering
  const { meMember, updateMember } = useFamilyMembers();
  const { linkedCalendarIds } = useMemberCalendarMapping(meMember?.id ?? null);

  const { profile, updateProfile } = useUserProfile();

  const [profilePictureUri, setProfilePictureUri] = useState<string | null>(null);
  const [preferredName, setPreferredName] = useState<string>('');
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isSavingAddress, setIsSavingAddress] = useState(false);

  // Default suggestion derived from the user's OAuth profile (e.g. "John" out
  // of "John Person" for Google sign-in). Used as the editor's initial value
  // when the user hasn't picked a preferred name yet.
  const authFirstName = getFirstName(getAuthFullName(session?.user?.user_metadata));
  const editorInitialName = preferredName || authFirstName;

  // Load profile picture and preferred name on mount
  useEffect(() => {
    const loadLocalData = async () => {
      try {
        // Load profile picture
        const picManager = ProfilePictureManager.getInstance();
        await picManager.initialize();
        const uri = picManager.getProfilePictureUri();
        setProfilePictureUri(uri);

        // Migrate: sync ProfilePictureManager URI to FamilyMember if different
        if (meMember?.id && uri !== meMember.profilePicture) {
          try {
            await updateMember(meMember.id, { profilePicture: uri });
          } catch (error) {
            console.error('[edit-profile] Failed to migrate profile picture:', error);
          }
        }

        // Load preferred name
        const nameManager = PreferredNameManager.getInstance();
        await nameManager.initialize();
        const name = nameManager.getPreferredName();
        setPreferredName(name || '');
      } finally {
        setIsLoading(false);
      }
    };

    loadLocalData();
  }, [meMember?.id, meMember?.profilePicture, updateMember]);

  const handlePhotoChanged = useCallback(async () => {
    const manager = ProfilePictureManager.getInstance();
    const uri = manager.getProfilePictureUri();
    setProfilePictureUri(uri);

    // Sync to FamilyMember for calendar avatar display
    if (meMember?.id) {
      try {
        await updateMember(meMember.id, { profilePicture: uri });
      } catch (error) {
        console.error('[edit-profile] Failed to sync profile picture to FamilyMember:', error);
      }
    }
  }, [meMember?.id, updateMember]);

  const handleSavePreferredName = async (name: string) => {
    console.log('[edit-profile] Saving preferred name locally:', name);

    try {
      setIsSaving(true);
      const manager = PreferredNameManager.getInstance();
      await manager.setPreferredName(name || null);
      setPreferredName(name);
      Alert.alert('Success', 'Preferred name updated successfully!');
    } catch (error) {
      console.error('[edit-profile] Failed to save preferred name:', error);
      Alert.alert('Error', 'Failed to save preferred name. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveHomeAddress = useCallback(async (address: string) => {
    try {
      setIsSavingAddress(true);
      await updateProfile({ homeAddress: address || null });
      Alert.alert('Success', 'Home address updated successfully!');
    } catch (error) {
      console.error('[edit-profile] Failed to save home address:', error);
      Alert.alert('Error', 'Failed to save home address. Please try again.');
    } finally {
      setIsSavingAddress(false);
    }
  }, [updateProfile]);

  const handleColorChanged = useCallback(async (color: string) => {
    if (meMember?.id) {
      try {
        await updateMember(meMember.id, { color });
      } catch (error) {
        console.error('[edit-profile] Failed to update color:', error);
      }
    }
  }, [meMember?.id, updateMember]);

  const handleManageCalendars = useCallback(() => {
    if (meMember?.id) {
      router.push(`/family-member/${meMember.id}/calendars`);
    }
  }, [router, meMember?.id]);

  const calendarLabel = linkedCalendarIds.length === 0
    ? 'No calendars linked'
    : `${linkedCalendarIds.length} calendar${linkedCalendarIds.length === 1 ? '' : 's'} linked`;

  const textColor = palette.text;
  const backgroundColor = palette.background;
  const accentColor = palette.tint;
  const placeholderColor = palette.tabIconDefault;

  if (isLoading) {
    return (
      <SafeAreaView style={[styles.loadingContainer, { backgroundColor }]}>
        <Stack.Screen
          options={{
            title: 'Edit Profile',
            headerShown: true,
          }}
        />
        <ActivityIndicator size="large" color={accentColor} />
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={[styles.container, { backgroundColor }]} edges={['bottom']}>
      <Stack.Screen
        options={{
          title: 'Edit Profile',
          headerShown: true,
        }}
      />

      <KeyboardAvoidingView
        behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
        style={styles.keyboardAvoid}>
        <ScrollView
          style={styles.scrollView}
          contentContainerStyle={styles.scrollContent}
          keyboardShouldPersistTaps="handled"
          showsVerticalScrollIndicator={false}>
          {/* Profile Photo Section */}
          <ProfilePhotoEditor
            profilePictureUri={profilePictureUri}
            accentColor={accentColor}
            textColor={textColor}
            backgroundColor={palette.cardBackground}
            onPhotoChanged={handlePhotoChanged}
          />

          {/* Color Picker Section */}
          {meMember && (
            <View style={styles.colorPickerSection}>
              <FamilyMemberColorPicker
                currentColor={meMember.color}
                onColorChanged={handleColorChanged}
                accentColor={accentColor}
                textColor={textColor}
                textMutedColor={placeholderColor}
                backgroundColor={palette.background}
                cardBackgroundColor={palette.cardBackground}
              />
            </View>
          )}

          {/* Filter Calendars Section */}
          {meMember && (
            <View style={styles.actionSection}>
              <TouchableOpacity
                style={[styles.actionCard, Shadows.sm, { backgroundColor: palette.cardBackground }]}
                onPress={handleManageCalendars}
                activeOpacity={0.7}
              >
                <View style={styles.actionRow}>
                  <View style={styles.actionLeft}>
                    <Ionicons name="calendar" size={20} color={accentColor} />
                    <View style={styles.actionInfo}>
                      <Text style={[styles.actionTitle, { color: textColor, fontFamily: getBodyFont() }]}>
                        Filter Calendars
                      </Text>
                      <Text style={[styles.actionSubtitle, { color: palette.textMuted, fontFamily: getBodyFont() }]}>
                        {calendarLabel}
                      </Text>
                    </View>
                  </View>
                  <Ionicons name="chevron-forward" size={20} color={palette.textMuted} />
                </View>
              </TouchableOpacity>
            </View>
          )}

          {/* Preferred Name Section */}
          <PreferredNameEditor
            initialName={editorInitialName}
            accentColor={accentColor}
            textColor={textColor}
            placeholderColor={placeholderColor}
            backgroundColor={palette.cardBackground}
            onSave={handleSavePreferredName}
            isSaving={isSaving}
          />

          {/* Home Address Section */}
          <HomeAddressEditor
            initialAddress={profile?.homeAddress ?? ''}
            accentColor={accentColor}
            textColor={textColor}
            placeholderColor={placeholderColor}
            backgroundColor={palette.cardBackground}
            onSave={handleSaveHomeAddress}
            isSaving={isSavingAddress}
          />

          {/* Notes Section */}
          {meMember && (
            <View style={styles.actionSection}>
              <NotesLinkRow
                notesCount={meMember.notes?.length ?? 0}
                onPress={() => router.push(`/family-member/${meMember.id}/notes`)}
              />
            </View>
          )}

        </ScrollView>
      </KeyboardAvoidingView>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  loadingContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  keyboardAvoid: {
    flex: 1,
  },
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    paddingHorizontal: Spacing.lg,
    paddingVertical: Spacing.sm,
  },
  colorPickerSection: {
    alignItems: 'center',
    marginBottom: Spacing.lg,
  },
  actionSection: {
    marginBottom: Spacing.lg,
  },
  actionCard: {
    borderRadius: BorderRadius.md,
    padding: Spacing.lg,
  },
  actionRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  actionLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: Spacing.md,
    flex: 1,
  },
  actionInfo: {
    flex: 1,
  },
  actionTitle: {
    fontSize: 16,
    fontWeight: '500',
  },
  actionSubtitle: {
    fontSize: 12,
    marginTop: Spacing.xxs,
  },
});
